
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Breadcrumb from '../../components/feature/Breadcrumb';

export default function Contact() {
  const [formData, setFormData] = useState({
    inquiryType: '',
    name: '',
    phone: '',
    email: '',
    message: '',
    privacyAgreed: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacyAgreed) {
      alert('プライバシーポリシーに同意してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('ご相談内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new URLSearchParams();
      submitData.append('inquiryType', formData.inquiryType);
      submitData.append('name', formData.name);
      submitData.append('phone', formData.phone);
      submitData.append('email', formData.email);
      submitData.append('message', formData.message);
      submitData.append('privacyAgreed', formData.privacyAgreed.toString());

      const response = await fetch('https://readdy.ai/api/form/d3gv3ph3290jal8ucleg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          inquiryType: '',
          name: '',
          phone: '',
          email: '',
          message: '',
          privacyAgreed: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <Breadcrumb />
      <main className="pt-8">
        {/* Hero Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
              お問い合わせ
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              車に関することなら、どんなことでもご相談ください。<br />
              お電話またはWebフォームからお気軽にお問い合わせいただけます。
            </p>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Webお問い合わせフォーム</h2>
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-2">
                      お問い合わせ種別 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-8"
                    >
                      <option value="">選択してください</option>
                      <option value="車検">車検</option>
                      <option value="修理">修理</option>
                      <option value="鈑金">鈑金</option>
                      <option value="乗り換え">乗り換え</option>
                      <option value="採用">採用</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="山田太郎"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="090-1234-5678"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      ご相談内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="お車の状況やご相談内容をお聞かせください"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {formData.message.length}/500文字
                    </p>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="privacyAgreed"
                      name="privacyAgreed"
                      checked={formData.privacyAgreed}
                      onChange={handleInputChange}
                      required
                      className="mt-1 mr-3 cursor-pointer"
                    />
                    <label htmlFor="privacyAgreed" className="text-sm text-gray-700 cursor-pointer">
                      プライバシーポリシーに同意する <span className="text-red-500">*</span>
                    </label>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                      お問い合わせありがとうございます。内容を確認の上、担当者よりご連絡いたします。
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                      送信に失敗しました。お手数ですが、お電話にてお問い合わせください。
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </Button>
                </form>
              </div>

              {/* Contact Info */}
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6">お電話でのお問い合わせ</h2>
                <div className="bg-gray-50 p-8 rounded-lg">
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-orange-600 mb-2">0120-000-000</div>
                    <p className="text-gray-600">受付時間: 9:00〜18:00（日曜・祝日除く）</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-time-line text-orange-500 text-xl mr-3"></i>
                      <div>
                        <p className="font-medium text-slate-800">営業時間</p>
                        <p className="text-gray-600">平日: 9:00-18:00 / 土曜: 9:00-17:00</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-calendar-line text-orange-500 text-xl mr-3"></i>
                      <div>
                        <p className="font-medium text-slate-800">定休日</p>
                        <p className="text-gray-600">日曜・祝日</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-phone-line text-orange-500 text-xl mr-3"></i>
                      <div>
                        <p className="font-medium text-slate-800">緊急時</p>
                        <p className="text-gray-600">24時間対応可能</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">よくあるお問い合わせ</h3>
                  <div className="space-y-3">
                    {[
                      '車検の予約はいつから可能ですか？',
                      '代車は借りられますか？',
                      '見積もりは無料ですか？',
                      '支払い方法は何がありますか？',
                      '土曜日も営業していますか？'
                    ].map((faq, index) => (
                      <div key={index} className="flex items-center text-gray-700">
                        <i className="ri-question-line text-orange-500 mr-3"></i>
                        <span className="text-sm">{faq}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Location */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-800 text-center mb-12">アクセス</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="bg-white p-8 rounded-lg shadow-md">
                  <h3 className="text-xl font-bold text-slate-800 mb-6">〇〇自動車整備工場</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <i className="ri-map-pin-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <div>
                        <p className="font-medium text-slate-800">住所</p>
                        <p className="text-gray-600">〒123-4567<br />東京都〇〇区〇〇町1-2-3</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-train-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <div>
                        <p className="font-medium text-slate-800">最寄り駅</p>
                        <p className="text-gray-600">JR〇〇線「〇〇駅」徒歩10分</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-car-line text-orange-500 text-xl mr-3 mt-1"></i>
                      <div>
                        <p className="font-medium text-slate-800">駐車場</p>
                        <p className="text-gray-600">お客様専用駐車場完備（10台）</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="bg-gray-200 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3240.8280649159!2d139.69170731525394!3d35.68948998019441!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188b563b00109f%3A0x337328def1e2ab26!2z5p2x5Lqs6aeF!5e0!3m2!1sja!2sjp!4v1635000000000!5m2!1sja!2sjp"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
